import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { filesApi } from '@/lib/api';
import Dropzone from './Dropzone';
import FilePreviewTile, { EmptySlotTile } from './FilePreviewTile';
import { describeFileLimitViolation } from '@/constants/utils/file_upload_limits';

/**
 * MultiImageUpload — multi-file uploader for image/file fields.
 *
 * Uses a shared dashed Dropzone (drag-and-drop on web) and renders rich preview
 * tiles for each picked file. When `slots` are provided (labeled positional
 * uploads, e.g. front/side/back), each slot gets a labeled tile and ghost
 * placeholders fill the empty positions so the layout stays predictable.
 *
 * Stores the full file metadata (id, name, mimeType, sizeInBytes, cdnPath,
 * thumbnailPath) so consumers can render previews without an extra fetch.
 */
function MultiImageUpload({
  value,
  onChange,
  disabled,
  accept = 'image/*',
  label = 'Add images',
  slots = [],
  limitsHint,
  maxSizeBytes,
  maxDurationSeconds,
}) {
  const { colors } = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  // Hidden input + active-slot ref let us reuse a single picker for any slot
  // click without spawning many input elements.
  const slotInputRef = useRef(null);
  const activeSlotRef = useRef(null);

  const files = useMemo(() => (Array.isArray(value) ? value : []).filter(Boolean), [value]);
  const slotMode = slots.length > 0;
  const maxFiles = slotMode ? slots.length : 12;

  const remaining = Math.max(0, maxFiles - files.length);
  const isFull = remaining === 0;

  const acceptingImages = (accept || '').includes('image');
  const helpText = slotMode
    ? `${files.length}/${maxFiles} slots filled`
    : files.length === 0
      ? (acceptingImages ? 'PNG, JPG, GIF, WebP — drag-and-drop supported' : 'Drag-and-drop supported')
      : isFull
        ? `Maximum reached (${maxFiles}). Remove a file to add another.`
        : `${files.length} added · ${remaining} more allowed`;
  const dropHelp = files.length === 0 && limitsHint
    ? `${helpText} · ${limitsHint}`
    : helpText;

  const styles = useMemo(() => StyleSheet.create({
    wrap: { gap: 12 },
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
    error: { marginTop: 2 },
  }), []);

  const commitFiles = useCallback((nextFiles) => {
    onChange?.(nextFiles.slice(0, maxFiles));
  }, [maxFiles, onChange]);

  const handleFiles = useCallback(async (picked) => {
    const list = Array.from(picked || []).filter(Boolean);
    if (!list.length) return;
    setError(null);
    setIsUploading(true);
    try {
      const uploaded = [];
      for (const file of list.slice(0, Math.max(1, maxFiles - files.length))) {
        const limitError = describeFileLimitViolation(file, { maxSizeBytes, maxDurationSeconds });
        if (limitError) {
          setError(limitError);
          continue;
        }
        const { file: saved } = await filesApi.uploadFile({ file });
        if (!saved?.id) throw new Error('Upload failed');
        uploaded.push({
          id: saved.id,
          name: saved.name || saved.originalName || file.name,
          mimeType: saved.mimeType || file.type,
          sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes ?? file.size,
          cdnPath: saved.cdnPath || null,
          thumbnailPath: saved.thumbnailPath || null,
          fileType: saved.fileType,
        });
      }
      commitFiles([...files, ...uploaded]);
    } catch (e) {
      setError(e?.message || 'Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  }, [commitFiles, files, maxFiles, maxSizeBytes, maxDurationSeconds]);

  // Library files are already uploaded — just shape and append.
  const handleLibrary = useCallback((picked) => {
    const next = [];
    for (const saved of (picked || []).slice(0, Math.max(1, maxFiles - files.length))) {
      const limitError = describeFileLimitViolation(saved, { maxSizeBytes, maxDurationSeconds });
      if (limitError) {
        setError(limitError);
        continue;
      }
      next.push({
        id: saved.id,
        name: saved.name || saved.originalName,
        mimeType: saved.mimeType,
        sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes,
        cdnPath: saved.cdnPath || null,
        thumbnailPath: saved.thumbnailPath || null,
        fileType: saved.fileType,
      });
    }
    if (next.length) commitFiles([...files, ...next]);
  }, [commitFiles, files, maxFiles, maxSizeBytes, maxDurationSeconds]);

  const removeAt = useCallback((index) => {
    commitFiles(files.filter((_, i) => i !== index));
  }, [commitFiles, files]);

  // ---------------------------------------------------------------------------
  // Slot-targeted picking. Clicking a slot (filled or empty) opens the picker
  // and replaces / fills exactly that slot — independent of the bottom dropzone
  // which always appends to the next-available slot.
  // ---------------------------------------------------------------------------
  const replaceAtSlot = useCallback(async (slotIdx, file) => {
    if (!file) return;
    setError(null);
    const limitError = describeFileLimitViolation(file, { maxSizeBytes, maxDurationSeconds });
    if (limitError) {
      setError(limitError);
      return;
    }
    setIsUploading(true);
    try {
      const { file: saved } = await filesApi.uploadFile({ file });
      if (!saved?.id) throw new Error('Upload failed');
      const next = files.slice();
      next[slotIdx] = {
        id: saved.id,
        name: saved.name || saved.originalName || file.name,
        mimeType: saved.mimeType || file.type,
        sizeInBytes: saved.sizeInBytes ?? saved.sizeBytes ?? file.size,
        cdnPath: saved.cdnPath || null,
        thumbnailPath: saved.thumbnailPath || null,
        fileType: saved.fileType,
      };
      commitFiles(next);
    } catch (e) {
      setError(e?.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, [commitFiles, files, maxSizeBytes, maxDurationSeconds]);

  const openSlotPicker = useCallback(async (slotIdx) => {
    if (disabled) return;
    activeSlotRef.current = slotIdx;
    if (Platform.OS === 'web') {
      slotInputRef.current?.click?.();
      return;
    }
    try {
      // On iOS, media accepts get an action sheet with a photo-library
      // option; the document picker alone can't browse the photo library.
      const { pickNativeFiles } = await import('@/constants/utils/nativeMediaPicker');
      const files = await pickNativeFiles({ accept, multiple: false });
      const file = files?.[0];
      if (!file) return;
      await replaceAtSlot(slotIdx, file);
    } catch {
      // Errors surface through replaceAtSlot's setError.
    }
  }, [accept, disabled, replaceAtSlot]);

  // ---------------------------------------------------------------------------
  // Slot mode: render each labeled position. Filled slots show a tile, empty
  // ones show a ghost placeholder. Single shared dropzone underneath fills the
  // next empty slot (and rolls over).
  // ---------------------------------------------------------------------------
  if (slotMode) {
    return (
      <View style={styles.wrap}>
        <View style={styles.grid}>
          {slots.map((slot, idx) => {
            const file = files[idx];
            return file ? (
              <View key={`${slot.label || 'slot'}-${idx}`} style={{ position: 'relative' }}>
                <FilePreviewTile
                  file={file}
                  isSample={!!file._placeholder}
                  slotLabel={slot.label}
                  onPress={disabled ? null : () => openSlotPicker(idx)}
                  onRemove={disabled ? null : () => removeAt(idx)}
                />
              </View>
            ) : (
              <EmptySlotTile
                key={`${slot.label || 'slot'}-${idx}`}
                label={slot.label}
                description={slot.description}
                onPress={disabled ? null : () => openSlotPicker(idx)}
                disabled={disabled}
              />
            );
          })}
        </View>
        <Dropzone
          accept={accept}
          multiple={remaining > 1}
          disabled={disabled}
          isUploading={isUploading}
          isFull={isFull}
          icon={acceptingImages ? 'image' : 'arrow-up-from-bracket'}
          primaryLabel={isFull ? 'All slots filled' : (label || 'Add image')}
          secondaryLabel={dropHelp}
          compact
          onFiles={handleFiles}
          onLibrary={handleLibrary}
        />
        {!!error && <Text size='s' color='danger' style={styles.error}>{error}</Text>}

        {Platform.OS === 'web' && (
          // eslint-disable-next-line react/forbid-elements
          <input
            ref={slotInputRef}
            type='file'
            accept={accept || undefined}
            multiple={false}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              const idx = activeSlotRef.current ?? 0;
              if (file) await replaceAtSlot(idx, file);
              e.target.value = '';
            }}
            style={{ display: 'none' }}
          />
        )}
      </View>
    );
  }

  // ---------------------------------------------------------------------------
  // Free-form multi mode: a hero dropzone above, preview grid below.
  // ---------------------------------------------------------------------------
  return (
    <View style={styles.wrap}>
      <Dropzone
        accept={accept}
        multiple
        disabled={disabled}
        isUploading={isUploading}
        isFull={isFull}
        icon={acceptingImages ? 'images' : 'cloud-arrow-up'}
        primaryLabel={isFull ? 'Maximum files reached' : (files.length ? (label || 'Add more') : (acceptingImages ? 'Drag and drop images here' : 'Drag and drop files here'))}
        secondaryLabel={dropHelp}
        compact={files.length > 0}
        onFiles={handleFiles}
        onLibrary={handleLibrary}
      />
      {files.length > 0 && (
        <View style={styles.grid}>
          {files.map((file, idx) => (
            <FilePreviewTile
              key={file.id || `placeholder-${idx}`}
              file={file}
              isSample={!!file?._placeholder}
              onRemove={disabled ? null : () => removeAt(idx)}
            />
          ))}
        </View>
      )}
      {!!error && <Text size='s' color='danger' style={styles.error}>{error}</Text>}
    </View>
  );
}

export default React.memo(MultiImageUpload);
